import { HubConnectionBuilder } from '@microsoft/signalr';
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { appInsight, collectProperties } from 'components/appInsight';
import { mutex } from 'components/refreshTokenEngine';
import { setIsOpenErrorModalValue } from 'features/modal/modalSlice';
import { setAllItems, setAllReels, setIncrementalItems } from 'features/slotArchive/slotArchiveSlice';
import { GamesSlotListType, ReelDetailsProps, SlotByListNameProps } from 'features/slotArchive/types';
import { allFilterString } from 'features/slotArchive/utils/utils';
import { getCurrentLocale } from 'hooks/useLingUI';
import { ActionMessageResponseApi } from 'lib/api/ActionMessageResponseApi';
import { BingoCatalogResponseApi } from 'lib/api/bingoCatalogResponseApi';
import { CardGamesCatalogResponseApi } from 'lib/api/cardGamesCatalogResponseApi';
import { CasinoCatalogResponseApi } from 'lib/api/casinoCatalogResponseApi';
import { CountMessageResponseApi } from 'lib/api/countMessagesResponseApi';
import { JumioUrlResponseApi } from 'lib/api/jumioUrlResponseApi';
import { LimitsResponseApi } from 'lib/api/limitsResponseApi';
import { LotteryRegistrationResponseApi } from 'lib/api/lotteriesRegistrationResponseApi';
import { MessageCategoryResponseApi } from 'lib/api/messageCategoryResponseApi';
import { MessageResponseApi } from 'lib/api/messagesResponseApi';
import { PokerCatalogResponseApi } from 'lib/api/pokerCatalogResponseApi';
import { PrivacyResponseApi } from 'lib/api/privacyResponseApi';
import { QuickSlotResponseApi } from 'lib/api/quickSlotResponseApi';
import { AutocompleteGameProps, SearchResponseApi } from 'lib/api/searchResponseApi';
import { FiltersResponseApi } from 'lib/api/slotFiltersResponseApi';
import { TreeFavouritesResponseApi } from 'lib/api/treeFavouritesResponseApi';
import {
  WalletPaymentLimits,
  WalletPaymentSnaiCard,
  WalletPaymentSnaiPay,
  WalletWithdrawalBonifico,
  WalletWithdrawalPostePay,
  WalletWithdrawalVaucher,
} from 'lib/api/walletResponseApi';
import { RootState } from 'lib/centralStore';
import { DatoMenuItemBlock } from 'lib/datoCms/types';
import { getUseSecondary } from 'lib/mwNodeSwitch';
import Persist, { StorageKind } from 'lib/persist';
import { CasinoLiveCatalog, Table } from 'types/casino-live/catalog-response';
import {
  GamesBingoLiveScheduleResDto,
  GamesBingoLiveWinnersRankingResDto,
  GamesSkillGameDto,
  GamesUrlLancioGiochiReqDto,
  UsersBaseRuleItemDtoFavoriteReqDto,
  UsersGetExtTokenResDto,
} from 'types/swagger';
import { GamesRoomDto, GamesSlotReelDetailsDto, GamesUrlLancioGiochiResponseDto } from 'types/swagger/games';
import { UsersBaseRuleItemDto, UsersContractResDto } from 'types/swagger/users';
import { isServerSide, TOKEN_BASE_NAME } from 'utility/constant';
import { detectMob, isClientSide, trasformMessagesCategoryToDatoMenuArrayBlock } from 'utility/functions';

const cookie = Persist(StorageKind.cookie);

const SlotByListNameEndPoint = 'games/externalgames/slot/list';

const baseQuery = fetchBaseQuery({
  baseUrl: `${process.env.NEXT_PUBLIC_API_BASE_URL}/`,
  credentials: 'include',
  mode: 'cors',
  prepareHeaders: (headers, { getState }) => {
    const { auth } = getState() as RootState;
    const token = auth.token?.accessToken ?? cookie.getItem(TOKEN_BASE_NAME);

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    const head = getUseSecondary();
    if (head) {
      headers.set(head.name, head.value);
    }
    headers.set('Frontend-Type', detectMob() ? '60' : '1');
    headers.set('Content-Type', 'application/json');

    const country = `${process.env.NEXT_PUBLIC_COUNTRY}`;
    headers.set('Country', country);

    const lang = getCurrentLocale();
    headers.set('Language', lang);

    // console.log(`baseQuery country=${country}, language=${lang}`);
    return headers;
  },
});

// function logoutOnExpired(date: number) {
//   const isExpired = isBefore(date, new Date());
//   return isExpired;
// }

// async function retrieveToken(): Promise<AuthTokenType> {
//   const tokenResponse = await fetch(`/api/auth/accessToken`, { method: 'POST' });
//   const data: AuthTokenType = await tokenResponse.json();
//   return Promise.resolve(data);
// }

const baseQueryExtended: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, options) => {
  // Skip token refresh on requests from the server
  if (isServerSide) {
    return await baseQuery(args, api, options);
  }

  if (mutex.isLocked()) {
    await mutex.waitForUnlock();
  }

  const response = await baseQuery(args, api, options);
  /* if (response.meta?.response?.status === 401) {
    let isLogged = isTruthy(cookie.getItem(IS_LOGGED));
    if (isLogged) {
      // we can mark token expired only if api call started after token
      // otherwise we sould invalidate a fresh token just because api started before
      const { auth, dashboard } = (api.getState() as RootState) ?? {};
      // if (tsCall > (auth?.token?.ts ?? 0)) {
      const isAlertMessageOpen = !!dashboard?.alertMessage;
      const refreshTokenErrorLabel = !isTruthy(cookie.getItem(WINDOW_CLOSED))
        ? i18n._('~refresh-token-error~')
        : i18n._('~session-expired~');
      if (!isAlertMessageOpen) {
        api.dispatch(
          setAlertMessage({
            icon: IconsEnum.CLOSE_CIRCLE,
            message: refreshTokenErrorLabel!,
            color: 'var(--color-warning)',
            animation: false,
          })
        );
      }
      await api.dispatch(signOut({dtNow: utcNow()}));
      // }
      // } else {
      //   // BASE TOKEN EXPIRED - GET NEW ONE
      //   const release = await mutex.acquire();
      //   const token = await retrieveToken();
      //   api.dispatch(setToken(token));
      //   release();
      //   return baseQueryExtended(args, api, options);
    }
  } else 
  */ if (
    response.error &&
    response.error?.status !== 'FETCH_ERROR' &&
    !response.meta?.request?.url?.includes('/movimenti/movimenti/cashout')
  ) {
    api.dispatch(
      setIsOpenErrorModalValue({
        isOpen: true,
        errorCode: response.error.status.toString(),
        retryAction: () => baseQueryExtended(args, api, options),
      })
    );
  }
  // if (isTruthy(cookie.getItem(WINDOW_CLOSED))) cookie.removeItem(WINDOW_CLOSED);
  return response;
};

// Define our single API slice object
export const apiSlice = createApi({
  // The cache reducer expects to be added at `state.api` (already default - this is optional)
  reducerPath: 'api',
  // All of our requests will have URLs starting with '/fakeApi'
  baseQuery: baseQueryExtended,
  tagTypes: [
    'Limits',
    'Wallet',
    'Privacy',
    'PrivacyForChat',
    'AuthData',
    'Messages',
    'JumioUrl',
    'Scommesse',
    'Wallet_Happybet',
    'Autolimiti_Happybet',
    'AutoPrelievo_Happybet',
    'CountMessages',
    'Manifestazioni',
    'ProfileContract',
    'ImpostazioniScommessa',
    'Movimenti',
  ],
  // The "endpoints" represent operations and requests for this server
  endpoints: (builder) => ({
    // The `getPosts` endpoint is a "query" operation that returns data
    getSlots: builder.query<GamesSlotListType, void>({
      // The URL for the request is '/fakeApi/posts'
      query: () => `games/externalgames/slot/top?topsize=20`,
    }),
    getSlot: builder.query<any, { id: string }>({
      // The URL for the request is '/fakeApi/posts'
      query: ({ id }) => `games/externalgames/slot/catalog/${id}`,
    }),
    getAutocompleteGame: builder.query<SearchResponseApi, AutocompleteGameProps>({
      keepUnusedDataFor: 0.1,
      query: ({ category, searchText }) => `games/externalgames/${category}/autocomplete?query=${searchText}`,
    }),
    getMightLikes: builder.query<{ cardList: Array<string> }, string>({
      // The URL for the request is '/fakeApi/posts'
      query: (slug) => `/users/user/potrebbepiacerti/?slug=${slug}`,
    }),
    getCasino: builder.query<CasinoCatalogResponseApi, void>({
      // The URL for the request is '/fakeApi/posts'
      query: () => `games/externalgames/casino/catalog`,
    }),
    getCasinoDetail: builder.query<GamesSkillGameDto, { slug: string }>({
      query: ({ slug }) => `games/externalgames/casino/catalog/${slug}`,
    }),
    getMostSearched: builder.query<SearchResponseApi, { category: string }>({
      keepUnusedDataFor: 0.1,
      query: ({ category }) => `games/externalgames/${category}/most-searched`,
    }),
    getQuickSlot: builder.query<QuickSlotResponseApi, void>({
      // The URL for the request is '/fakeApi/posts'
      query: () => `games/externalgames/slot/quick`,
    }),
    getAllCasinoLive: builder.query<CasinoLiveCatalog, void>({
      query: () => `games/externalgames/casinolive/catalog`,
    }),
    getSlotByIdList: builder.mutation<GamesSlotListType, string[]>({
      // The URL for the request is '/fakeApi/posts'
      query: (idList) => ({
        url: SlotByListNameEndPoint,
        body: idList,
        method: 'POST',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
        },
      }),
    }),
    getCasinoTableLive: builder.query<CasinoLiveCatalog, void>({
      query: () => `games/externalgames/casinolive/catalog`,
      async onCacheEntryAdded(args, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const connection = new HubConnectionBuilder()
          .withUrl(`${process.env.NEXT_PUBLIC_API_BASE_URL}/signalr-games/sigr-hubs/casinolive-tables`)
          .withAutomaticReconnect()
          .build();
        await connection.start();
        try {
          await cacheDataLoaded;
          const listener = (fullContent: boolean, path: string, message: string) => {
            const data: Table = JSON.parse(message);

            updateCachedData((draft) => {
              const index = (draft?.games ?? []).findIndex((e) => data.id === e.id);
              // HANDLE UPDATE OR PUSH NEW GAME
              index !== -1 ? (draft.games[index] = data) : draft.games.push(data);
            });
          };

          connection.on('tableupdate', listener);
        } catch (error) {
          console.error(error);
        }
        await cacheEntryRemoved;
        connection.stop();
      },
    }),
    getSlotByListName: builder.query<GamesSlotListType, SlotByListNameProps>({
      query: ({ pageNumber, pageSize, selectedFilters, listName }) => {
        const filters = selectedFilters ? allFilterString(selectedFilters) : '';
        return `${SlotByListNameEndPoint}?list=${encodeURIComponent(listName)}&pageNumber=${encodeURIComponent(
          pageNumber
        )}&pageSize=${encodeURIComponent(pageSize)}${filters !== '' ? `${decodeURIComponent(filters)}` : filters}`;
      },
      async onQueryStarted({ pageNumber, pageSize, selectedFilters, listName }, { queryFulfilled, dispatch }) {
        try {
          if (pageNumber > 1) {
            const { data } = await queryFulfilled;
            dispatch(setIncrementalItems(data?.games ?? []));
          } else {
            dispatch(setAllItems([]));
            dispatch(setAllReels([]));
            const { data } = await queryFulfilled;
            dispatch(setAllItems(data?.games ?? []));
            dispatch(setAllReels(data?.reels ?? []));
          }
        } catch (exception) {
          const filters = selectedFilters ? allFilterString(selectedFilters) : '';
          appInsight?.trackException({
            exception,
            id: `${SlotByListNameEndPoint}?list=${listName}&pageNumber=${pageNumber}&pageSize=${pageSize}${
              filters !== '' ? `&${filters}` : filters
            }`,
            properties: collectProperties(true),
          });
        }
      },
    }),
    getReelDetails: builder.query<GamesSlotReelDetailsDto, ReelDetailsProps>({
      query: ({ listName, slug }) => {
        return `games/externalgames/slot/reel/details?list=${encodeURIComponent(listName)}&slug=${slug}`;
      },
      async onQueryStarted({ listName, slug }, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setAllItems(data.games ?? []));
        } catch (exception) {
          appInsight?.trackException({
            exception,
            id: `games/externalgames/slot/reel/details?list=${encodeURIComponent(listName)}&slug=${slug}`,
          });
        }
      },
    }),
    getCardGames: builder.query<CardGamesCatalogResponseApi, void>({
      // The URL for the request is '/fakeApi/posts'
      query: () => `games/externalgames/cardgame/catalog`,
    }),
    getCardGame: builder.query<GamesSkillGameDto, string>({
      query: (slug) => `games/externalgames/cardgame/catalog/${slug}`,
    }),
    getAllBingo: builder.query<BingoCatalogResponseApi, void>({
      query: () => `games/externalgames/bingo/catalog`,
    }),
    getBingoToken: builder.query<{ token: string }, void>({
      query: () => `games/externalgames/bingo/token`,
    }),
    getPoker: builder.query<PokerCatalogResponseApi, void>({
      // The URL for the request is '/fakeApi/posts'
      query: () => `games/externalgames/poker/catalog`,
    }),
    getExternalToken: builder.query<
      UsersGetExtTokenResDto,
      {
        codicePiattaforma: number;
        codeGame?: string;
      }
    >({
      query({ codicePiattaforma, codeGame }) {
        if (!codeGame) {
          return {
            url: `users/user/externalToken?codicePiattaforma=${codicePiattaforma}`,
          };
        }
        return {
          url: `users/user/externalToken?codicePiattaforma=${codicePiattaforma}&codeGame=${codeGame}`,
        };
      },
    }),
    getExternalTokenPoker: builder.query<{ token: string }, null>({
      query() {
        return {
          url: `games/externalGames/poker/token`,
        };
      },
    }),
    getSlotFiltersByCategory: builder.query<
      FiltersResponseApi,
      {
        listName: string;
      }
    >({
      query: ({ listName }) => {
        return `games/externalgames/slot/filters?list=${listName}`;
      },
    }),
    getRegistrationLotteries: builder.mutation<
      LotteryRegistrationResponseApi,
      { lotteryType: string; value: number; authorization: string }
    >({
      query: (res) => ({
        url: `users/user/registrazionelotteria`,
        method: 'POST',
        headers: {
          Authorization: res.authorization,
          'Content-type': 'application/json',
        },
        body: {
          operationType: '1',
          tipoLotteria: res.lotteryType,
        },
      }),
    }),
    postFavourite: builder.mutation<void, UsersBaseRuleItemDtoFavoriteReqDto>({
      query: (body) => ({
        url: `users/user/favorites/games`,
        body,
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
      }),
    }),
    postFavouriteBySection: builder.mutation<void, UsersBaseRuleItemDtoFavoriteReqDto>({
      query: (body) => ({
        url: `users/user/favorites/${body.verticale}/${decodeURIComponent(body?.rule?.id ?? '')}`,
        body,
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
      }),
    }),
    deleteFavouriteBySection: builder.mutation<void, UsersBaseRuleItemDtoFavoriteReqDto>({
      query: (body) => ({
        url: `users/user/favorites/${body.verticale}/${decodeURIComponent(body?.rule?.id ?? '')}`,
        body,
        method: 'DELETE',
        headers: {
          'Content-type': 'application/json',
        },
      }),
    }),
    getProfileContract: builder.query<UsersContractResDto, string | undefined>({
      query: (csmfCod) => ({
        url: `users/user/profile/contract`,
        method: 'POST',
        body: {
          carta: '', // obbligatorio, ma lasciare vuoto
          csmf_cod: csmfCod, // ottenuto dalla getprofile
        },
      }),
      providesTags: ['ProfileContract'],
    }),
    /* getAuthData: builder.query<AuthDataApi, void | undefined>({
      query: () => ({
        url: `users/user/authdata`,
        method: 'get',
      }),
      providesTags: ['AuthData'],
    }), */
    getMessages: builder.query<
      MessageResponseApi[],
      {
        category: string | null;
        isArchived: boolean | null;
        unreaded: boolean | null;
      }
    >({
      query: ({ category, isArchived, unreaded }) => ({
        url: `users/user/inbox`,
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: {
          category,
          isArchived,
          unreaded,
        },
      }),
      providesTags: ['Messages'],
    }),
    getMessageCategories: builder.query<DatoMenuItemBlock[], string>({
      query: (_: string) => `users/user/inbox/categories`,
      transformResponse: (response: MessageCategoryResponseApi[], meta, arg): DatoMenuItemBlock[] => {
        let modifiedCategories: DatoMenuItemBlock[] = [
          {
            id: '',
            label: arg,
            linkBlock: [{ id: '', href: '' }],
          },
        ];
        if (response.length !== 0) {
          modifiedCategories = modifiedCategories.concat(trasformMessagesCategoryToDatoMenuArrayBlock(response));
        }
        return [...modifiedCategories];
      },
    }),
    callActionMessage: builder.mutation<ActionMessageResponseApi, { id: string; action: string }>({
      query: ({ id, action }) => ({
        url: `users/user/inbox/${id}?action=${action}`,
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
      }),
      invalidatesTags: ['Messages', 'CountMessages'],
    }),
    editContractProfile: builder.mutation<any, {}>({
      query: (profileContractRequest) => ({
        url: `users/user/profile/contract/edit`,
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: {
          ...profileContractRequest,
        },
      }),
      invalidatesTags: ['ProfileContract', 'PrivacyForChat'],
    }),
    getCountMessages: builder.query<CountMessageResponseApi, void>({
      query: () => `users/user/inbox/count`,
      providesTags: ['CountMessages'],
    }),
    getPrivacy: builder.query<PrivacyResponseApi, void>({
      query: () => `users/user/profile/privacy`,
      providesTags: ['Privacy'],
    }),
    postPrivacy: builder.mutation<
      any,
      {
        carta?: string;
        flag_marketing?: string;
        flag_profilazione?: string;
        flag_chat?: string;
      }
    >({
      query: (privacyRequest) => ({
        url: `users/user/profile/privacy`,
        method: 'POST',
        body: privacyRequest,
      }),
      invalidatesTags: ['ProfileContract', 'PrivacyForChat'],
    }),
    getPrivacyForChat: builder.query<PrivacyResponseApi, void>({
      query: () => `users/user/profile/privacy`,
      providesTags: ['PrivacyForChat'],
    }),
    getLimits: builder.query<LimitsResponseApi[], string | undefined>({
      query: (csmfCod) => ({
        url: `users/user/profile/limits`,
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: {
          carta: '', // obbligatorio, ma lasciare vuoto
          csmf_cod: csmfCod, // ottenuto dalla getprofile
        },
      }),
      providesTags: ['Limits'],
    }),
    editLimit: builder.mutation<any, {}>({
      query: (limit_request) => ({
        url: `users/user/profile/limit`,
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: {
          ...limit_request,
        },
      }),
      invalidatesTags: ['Limits'],
    }),
    editPassword: builder.mutation<any, { username?: string | null; password: string }>({
      query: ({ username, password }) => ({
        url: `users/user/profile/password`,
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: {
          carta: '',
          username,
          password,
        },
      }),
    }),
    editAutoexclusion: builder.mutation<any, { tipo: string; periodo: string }>({
      query: ({ tipo, periodo }) => ({
        url: `users/user/profile/lock`,
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: {
          tipo, // TemporaneaTrasversale,PermanenteTrasversale
          periodo, // OneMonth,TwoMonths,ThreeMonths,SixMonths,OneYear
        },
      }),
    }),
    getJumioUrl: builder.query<JumioUrlResponseApi, void>({
      query: () => `users/user/profile/contract/id/validate`,
      providesTags: ['JumioUrl'],
    }),
    getUnauthenticatedJumioUrl: builder.query<JumioUrlResponseApi, { carta: string }>({
      query: ({ carta }) => `users/user/Profile/Contract/Id/Validate/NoUserAuth?carta=${carta}`,
      providesTags: ['JumioUrl'],
    }),
    getFavourites: builder.query<TreeFavouritesResponseApi, void>({
      query: () => `users/user/favorites/tree`,
    }),
    getFavouritesBySection: builder.query<UsersBaseRuleItemDto[], string>({
      query: (section) => `users/user/favorites/${section}`,
    }),
    setWalletWithdrawalVaucher: builder.mutation<
      WalletWithdrawalVaucher,
      {
        idUserSession: string;
        importo: number;
      }
    >({
      query: ({ idUserSession, importo }) => ({
        url: `users/user/wallet/withdrawal/voucher`,
        method: 'POST',
        body: {
          idUserSession: idUserSession,
          importo: importo,
        },
      }),
    }),

    setWalletPaymentSnaiCard: builder.mutation<
      WalletPaymentSnaiCard,
      {
        codiceCsmf: string;
        codiceBonus: string;
        codiceSnaicard: string;
        codiceCategoriaDeposito?: number;
      }
    >({
      query: ({ codiceCsmf, codiceBonus, codiceSnaicard, codiceCategoriaDeposito }) => ({
        url: `users/user/wallet/payment/snaicard`,
        method: 'POST',
        body: {
          codiceCsmf: codiceCsmf,
          codiceBonus: codiceBonus,
          codiceSnaicard: codiceSnaicard,
          codiceCategoriaDeposito,
        },
      }),
    }),

    setWalletPaymentSnaiPay: builder.mutation<
      WalletPaymentSnaiPay,
      {
        codiceCsmf: string;
        idUserSession: string;
        codiceBonus: string;
        codiceSnaipay: string;
      }
    >({
      query: ({ codiceCsmf, idUserSession, codiceBonus, codiceSnaipay }) => ({
        url: `users/user/wallet/payment/snaipay`,
        method: 'POST',
        body: {
          codiceCsmf: codiceCsmf,
          idUserSession: idUserSession,
          codiceBonus: codiceBonus,
          codiceSnaipay: codiceSnaipay,
        },
      }),
    }),

    getPaymentLimits: builder.query<WalletPaymentLimits, { catDeposito?: number } | void>({
      query: (queryProps) =>
        queryProps?.catDeposito
          ? `users/user/wallet/payment/limits?tipoDeposito=${queryProps.catDeposito}`
          : `users/user/wallet/payment/limits`,
    }),

    setWalletWithdrawalPostePay: builder.mutation<
      WalletWithdrawalPostePay,
      {
        idUserSession: string;
        orderingName: string;
        orderingSurname: string;
        orderingFiscalCode: string;
        beneficiaryName: string;
        beneficiarySurname: string;
        beneficiaryFiscalCode: string;
        postepayPAN: string; //5333171000014221 TEST CARD
        importo: number;
      }
    >({
      query: ({
        idUserSession,
        orderingName,
        orderingSurname,
        orderingFiscalCode,
        beneficiaryName,
        beneficiarySurname,
        beneficiaryFiscalCode,
        postepayPAN,
        importo,
      }) => ({
        url: `users/user/wallet/withdrawal/poste`,
        method: 'POST',
        body: {
          idUserSession: idUserSession,
          orderingName: orderingName,
          orderingSurname: orderingSurname,
          orderingFiscalCode: orderingFiscalCode,
          beneficiaryName: beneficiaryName,
          beneficiarySurname: beneficiarySurname,
          beneficiaryFiscalCode: beneficiaryFiscalCode,
          postepayPAN: postepayPAN, //5333171000014221 TEST CARD
          importo: importo,
        },
      }),
    }),

    setWalletWithdrawalBonifico: builder.mutation<
      WalletWithdrawalBonifico,
      {
        csmfcod: number;
        importo: number;
        modalitaPrelievo: string;
        codiceTipoDoc: number;
        numeroDoc: string;
        tipologiaBonifico: string;
        email: string;
        telefono: string;
        iban: string | undefined;
        indirizzo: string | undefined;
        swiftBicCode: string | undefined;
        username: string;
        cf: string;
      }
    >({
      query: ({
        csmfcod,
        importo,
        modalitaPrelievo,
        codiceTipoDoc,
        numeroDoc,
        tipologiaBonifico,
        email,
        telefono,
        iban,
        indirizzo,
        swiftBicCode,
        username,
        cf,
      }) => ({
        url: `users/user/wallet/withdrawal/bonifico`,
        method: 'POST',
        body: {
          codiceCsmf: csmfcod,
          importo: importo,
          modalitaPrelievo: modalitaPrelievo,
          codiceTipoDoc: codiceTipoDoc,
          numeroDoc: numeroDoc,
          tipoBonifico: tipologiaBonifico,
          email: email,
          telefono: telefono,
          iban: iban,
          indirizzo: indirizzo,
          swiftBic: swiftBicCode || '',
          username: username,
          cf: cf,
        },
      }),
    }),

    setWalletOktoBind: builder.mutation<
      void,
      {
        codiceCsmf: number | undefined;
        email: string;
        idRequest: string;
        telefono: string;
      }
    >({
      query: ({ codiceCsmf, email, telefono }) => ({
        url: `users/user/wallet/payment/okto/bind`,
        method: 'POST',
        body: {
          codiceCsmf: codiceCsmf, // ottenuto dalla getprofile
          email: email, // ottenuto dalla getprofile
          idRequest: null, //idRequest, // nullable
          telefono: telefono,
          retry: 0,
        },
      }),
    }),

    setWalletOktoPayment: builder.mutation<
      void,
      {
        codiceCsmf: string;
        email: string;
        idRequest: string;
        importo: number;
        codiceBonus: string;
      }
    >({
      query: ({ codiceCsmf, email, idRequest, importo, codiceBonus }) => ({
        url: `users/user/wallet/payment/okto`,
        method: 'POST',
        body: {
          codiceCsmf: codiceCsmf,
          email: email,
          idRequest: idRequest,
          importo: importo,
          codiceBonus: codiceBonus,
        },
      }),
    }),

    setWalletOktoWithdrawal: builder.mutation<
      void,
      {
        codiceCsmf: string;
        email: string;
        idRequest: string;
        importo: number;
        codiceBonus: string;
      }
    >({
      query: ({ codiceCsmf, email, idRequest, importo, codiceBonus }) => ({
        url: `users/user/wallet/withdrawal/okto`,
        method: 'POST',
        body: {
          codiceCsmf: codiceCsmf,
          email: email,
          idRequest: idRequest,
          importo: importo,
          codiceBonus: codiceBonus,
        },
      }),
    }),
    getBingoLiveWinnersRanking: builder.query<GamesBingoLiveWinnersRankingResDto, void>({
      query: () => `games/externalgames/bingolive/winnersranking`,
    }),
    getBingoLiveSchedule: builder.query<GamesBingoLiveScheduleResDto, void>({
      query: () => `games/externalgames/bingolive/schedule`,
    }),
    getUrlLaunchGame: builder.query<GamesUrlLancioGiochiResponseDto, GamesUrlLancioGiochiReqDto>({
      query: (data) => ({
        url: `games/externalgames/lanciogiochi`,
        method: 'POST',
        body: data,
      }),
    }),
    getBingoLiveRooms: builder.query<GamesRoomDto, void>({
      query: () => `games/externalgames/games/getrooms`,
    }),
  }),
});

// Export the auto-generated hook for the `getPosts` query endpoint
export const {
  useGetSlotQuery,
  useGetSlotsQuery,
  useGetPokerQuery,
  useGetCasinoQuery,
  useGetCardGamesQuery,
  useLazyGetSlotsQuery,
  useLazyGetQuickSlotQuery,
  useGetMightLikesQuery,
  useGetReelDetailsQuery,
  useGetMostSearchedQuery,
  useLazyGetAllBingoQuery,
  useGetAllCasinoLiveQuery,
  useLazyGetBingoTokenQuery,
  useGetSlotByListNameQuery,
  useGetBingoLiveRoomsQuery,
  useLazyGetReelDetailsQuery,
  useGetCasinoTableLiveQuery,
  useGetSlotByIdListMutation,
  useLazyGetAllCasinoLiveQuery,
  useLazyGetSlotByListNameQuery,
  useGetCasinoDetailQuery,
  useLazyGetExternalTokenQuery,
  useLazyGetSlotFiltersByCategoryQuery,
  useGetRegistrationLotteriesMutation,
  usePostFavouriteMutation,
  useGetProfileContractQuery,
  useLazyGetProfileContractQuery,
  useGetMessagesQuery,
  useGetMessageCategoriesQuery,
  useCallActionMessageMutation,
  useEditContractProfileMutation,
  useGetCountMessagesQuery,
  useGetPrivacyQuery,
  useGetPrivacyForChatQuery,
  useGetLimitsQuery,
  useEditLimitMutation,
  useEditPasswordMutation,
  useEditAutoexclusionMutation,
  useGetJumioUrlQuery,
  useLazyGetJumioUrlQuery,
  useGetUnauthenticatedJumioUrlQuery,
  useLazyGetUnauthenticatedJumioUrlQuery,
  useGetFavouritesQuery,
  useGetCardGameQuery,
  useLazyGetCardGameQuery,
  useSetWalletWithdrawalVaucherMutation,
  useSetWalletPaymentSnaiCardMutation,
  useSetWalletPaymentSnaiPayMutation,
  useSetWalletWithdrawalPostePayMutation,
  useGetPaymentLimitsQuery,
  useSetWalletWithdrawalBonificoMutation,
  useSetWalletOktoBindMutation,
  useSetWalletOktoPaymentMutation,
  useSetWalletOktoWithdrawalMutation,
  usePostFavouriteBySectionMutation,
  useGetFavouritesBySectionQuery,
  useDeleteFavouriteBySectionMutation,
  useGetBingoLiveWinnersRankingQuery,
  useGetBingoLiveScheduleQuery,
  useLazyGetUrlLaunchGameQuery,
  usePostPrivacyMutation,
} = apiSlice;
